import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useLang } from "@/components/LanguageSelector";

// TranslatedProse — wrap any article body. On language change, walks the
// subtree, batches every text node into a single /api/translate call using a
// distinctive separator, then reinserts translated fragments back into place.
// English restores from the originals map.

const SEP = "\n<<<::SP-SEP::>>>\n";

export default function TranslatedProse({ children, cacheKey }) {
  const ref = useRef(null);
  const originalsRef = useRef(new Map()); // TextNode -> original string
  const lang = useLang();
  const [status, setStatus] = useState("idle"); // idle | loading | translated | error

  useEffect(() => {
    if (!ref.current) return;
    // 1. Collect every non-empty text node
    const walker = document.createTreeWalker(ref.current, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
      const n = walker.currentNode;
      const t = (n.nodeValue || "").trim();
      if (t && t.length > 1) {
        if (!originalsRef.current.has(n)) originalsRef.current.set(n, n.nodeValue);
        nodes.push(n);
      }
    }
    if (nodes.length === 0) return;

    // 2. English -> restore originals
    if (lang === "en") {
      nodes.forEach((n) => {
        const orig = originalsRef.current.get(n);
        if (orig !== undefined) n.nodeValue = orig;
      });
      setStatus("idle");
      return;
    }

    // 3. Translate everything in one call (server caches by hash + lang)
    const originals = nodes.map((n) => originalsRef.current.get(n) || n.nodeValue);
    const joined = originals.join(SEP);
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const { data } = await http.post("/translate", {
          text: joined,
          target_lang: lang,
          cache_key: cacheKey,
        });
        if (cancelled) return;
        const parts = (data.translated_text || "").split(SEP);
        // Best-effort splice: if the model kept the separator (usually it
        // does), we get 1:1 back. Otherwise we just skip mismatched entries.
        nodes.forEach((n, i) => {
          const t = parts[i];
          if (t && typeof t === "string") n.nodeValue = t;
        });
        setStatus("translated");
      } catch (e) {
        console.warn("Translate failed", e);
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [lang, cacheKey]);

  return (
    <div ref={ref} data-testid={`translated-prose-${cacheKey}`}>
      {status === "loading" && (
        <div className="fixed top-20 right-6 z-[80] font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)] bg-[color:var(--np-bg)] border border-black/20 px-3 py-1.5">
          Translating…
        </div>
      )}
      {children}
    </div>
  );
}
